import { Editor, useEditorState } from '@tiptap/react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useEditorStore, getPageDims, getEffectiveMarginMm } from '../store';
import { downloadPdf, exportPdfBlob } from '../io';
import { formatSeplReferenceNumber } from '../../../../utils/helpers';
import {
  DraftSignaturePlacement,
  LETTER_PAGE_HEIGHT_PX,
  LETTER_PAGE_WIDTH_PX,
  computeApprovedSignatureFitScale,
  normalizeSignatureHtmlForRender,
  sanitizeDraftSignaturePlacement,
} from '../../signatureLayoutShared';

interface SignaturePlacementConfig {
  markAllPages: boolean;
  base: DraftSignaturePlacement;
  overrides: Record<string, DraftSignaturePlacement>;
  skippedPages?: number[];
}

/**
 * Editor-accurate PDF exporter registered by the preview pane.
 * - `'download'` (default): saves the PDF to disk (existing behaviour).
 * - `'blob'`: resolves with the generated PDF Blob so read-only consumers can
 *   build a File for share / ZIP bundles from the exact same renderer.
 */
export interface PdfDownloadOptions {
  printSafeMarginMm?: number;
}

export type PdfDownloaderFn = (mode?: 'download' | 'blob', options?: PdfDownloadOptions) => Promise<void | Blob>;

interface Props {
  editor: Editor | null;
  qrCodeData?: string | null;
  letterNumber?: string | null;
  letterDate?: string | null;
  approvedSignatureHtml?: string | null;
  signaturePlacementConfig?: SignaturePlacementConfig | null;
  enableDraftSignatureDrag?: boolean;
  onDraftSignaturePlacementChange?: (next: DraftSignaturePlacement) => void;
  onRemoveSignaturePage?: (pageIndex: number) => void;
  registerPdfDownloader?: (fn: PdfDownloaderFn | null) => void;
}

const mmToPx = (mm: number) => (mm * 96) / 25.4;
const PAGE_GAP_PX = 24;
// Must match EditorSurface so the preview paginates identically.
const HEADER_ZONE_FALLBACK_MM = 28;
const HEADER_META_TOP_MM = 6;
const HEADER_META_HEIGHT_MM = 8;
const CONTENT_AFTER_META_MM = HEADER_META_TOP_MM + HEADER_META_HEIGHT_MM; // 12mm
const FOOTER_ZONE_FALLBACK_MM = 13;
// Must match EditorSurface/io.ts: breathing gap between body content and the
// letterhead/footer art so lines aren't clipped behind them in the PDF.
const CONTENT_VCLEARANCE_MM = 3;
const CONTENT_LEFT_MM = 15;
const CONTENT_RIGHT_MM = 15;
const QR_BOX_SIZE_PX = 64;
const QR_BOX_PADDING_PX = 0;
const QR_INSET_PX = 28;
const AUTO_GENERATED_TEXT = 'Auto-generated';
const DEFAULT_SIGNATURE_PLACEMENT: DraftSignaturePlacement = {
  pageIndex: 0,
  x: 56,
  y: 850,
  width: 168,
  height: 140,
};

const formatLetterDate = (value?: string | null) => {
  if (!value || typeof value !== 'string') return AUTO_GENERATED_TEXT;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return AUTO_GENERATED_TEXT;
  const dd = String(parsed.getDate()).padStart(2, '0');
  const mm = String(parsed.getMonth() + 1).padStart(2, '0');
  const yyyy = parsed.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

/**
 * Live preview pane. Mirrors the editor's *rendered* DOM (including any
 * pagination spacer widgets injected by the Pagination plugin) so the
 * preview shows the same page breaks the user sees in the editor. The
 * preview lays out N page-bg tiles behind a single content overlay,
 * matching the editor's structure, and alternates letterheads (page 1 uses
 * letterhead.png, pages 2+ use letterhead2.png).
 */
export default function PreviewPane({ editor, qrCodeData = null, letterNumber = null, letterDate = null, approvedSignatureHtml = null, signaturePlacementConfig = null, enableDraftSignatureDrag = false, onDraftSignaturePlacementChange, onRemoveSignaturePage, registerPdfDownloader }: Props) {
  const { title, pageSize, orientation, margins, marginMm, paginated, previewZoom, previewZoomIn, previewZoomOut, previewZoomReset } = useEditorStore();
  const dims = getPageDims(pageSize, orientation);
  const m = getEffectiveMarginMm(margins, marginMm);
  const referenceNo = formatSeplReferenceNumber(letterNumber) || AUTO_GENERATED_TEXT;
  const displayDate = formatLetterDate(letterDate);

  // Pull the rendered ProseMirror DOM (includes spacer widgets) so the
  // preview mirrors the editor exactly. Falls back to getHTML if view
  // isn't mounted yet.
  const html = useEditorState({
    editor,
    selector: (ctx) => {
      const e = ctx.editor;
      if (!e) return '';
      return e.view?.dom?.innerHTML ?? e.getHTML();
    }
  });

  const contentRef = useRef<HTMLDivElement>(null);
  const [pageCount, setPageCount] = useState(1);
  const [letterheadSafePx, setLetterheadSafePx] = useState(mmToPx(HEADER_ZONE_FALLBACK_MM));
  const [footerSafePx, setFooterSafePx] = useState(mmToPx(FOOTER_ZONE_FALLBACK_MM));
  const [dragState, setDragState] = useState<{ pageIndex: number; dx: number; dy: number } | null>(null);
  // Top reserve in paginated mode covers letterhead clearance only; the
  // 14mm SEPL meta band reservation moves into the editor flow as a
  // first-child padding so only page 1 has it.
  const topReservePx = letterheadSafePx + mmToPx(CONTENT_VCLEARANCE_MM);
  const bottomReservePx = footerSafePx + mmToPx(CONTENT_VCLEARANCE_MM);
  const contentPerPagePx = Math.max(1, mmToPx(dims.h) - topReservePx - bottomReservePx);

  const handleLetterheadLoad = (img: HTMLImageElement) => {
    // See EditorSurface for rationale: derive height from intrinsic ratio *
    // logical page width so we're not affected by the preview-stack scale
    // transform or by cached-image load timing.
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    if (!nw || !nh) return;
    const cssH = mmToPx(dims.w) * (nh / nw);
    if (cssH > 0) setLetterheadSafePx((prev) => (Math.abs(prev - cssH) > 0.5 ? cssH : prev));
  };

  const handleFooterLoad = (img: HTMLImageElement) => {
    // Mirror EditorSurface: footer renders edge-to-edge at natural aspect.
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    if (!nw || !nh) return;
    const cssH = mmToPx(dims.w) * (nh / nw);
    if (cssH > 0) setFooterSafePx((prev) => (Math.abs(prev - cssH) > 0.5 ? cssH : prev));
  };

  // Recompute preview page count from the rendered content height.
  useLayoutEffect(() => {
    if (!paginated) {
      setPageCount(1);
      return;
    }
    const el = contentRef.current;
    if (!el) return;
    const pageStride = mmToPx(dims.h) + PAGE_GAP_PX;

    const recalc = () => {
      const h = el.scrollHeight;
      if (pageStride <= 0) {
        setPageCount(Math.max(1, Math.ceil((h + PAGE_GAP_PX - 2) / (contentPerPagePx + PAGE_GAP_PX))));
        return;
      }
      // Stride-based count (assumes pagination spacers have snapped content to
      // page boundaries) combined with a content-fit lower bound so the last
      // block can never overflow past the final page tile. Mirrors the editor
      // surface logic — keeps the preview AND the exported PDF page count from
      // dropping the trailing content.
      const stridePages = Math.floor((h - 2) / pageStride) + 1;
      const fitPages = Math.ceil((h - contentPerPagePx) / pageStride) + 1;
      setPageCount(Math.max(1, stridePages, fitPages));
    };
    recalc();
    const ro = new ResizeObserver(recalc);
    ro.observe(el);
    return () => ro.disconnect();
  }, [paginated, contentPerPagePx, html]);

  const normalizedApprovedSignatureHtml = useMemo(
    () => normalizeSignatureHtmlForRender(approvedSignatureHtml),
    [approvedSignatureHtml]
  );

  const effectiveSignatureConfig = useMemo(() => {
    if (!signaturePlacementConfig) return null;
    const maxIndex = Math.max(0, pageCount - 1);
    const overrides: Record<string, DraftSignaturePlacement> = {};
    Object.entries(signaturePlacementConfig.overrides || {}).forEach(([key, value]) => {
      overrides[String(Math.max(0, Number(key) || 0))] = sanitizeDraftSignaturePlacement(value, DEFAULT_SIGNATURE_PLACEMENT);
    });

    const base = sanitizeDraftSignaturePlacement(signaturePlacementConfig.base, DEFAULT_SIGNATURE_PLACEMENT);
    return {
      markAllPages: Boolean(signaturePlacementConfig.markAllPages),
      base: {
        ...base,
        pageIndex: signaturePlacementConfig.markAllPages
          ? Math.min(Math.max(0, Number(base.pageIndex) || 0), maxIndex)
          : maxIndex,
      },
      overrides,
      skippedPages: Array.isArray(signaturePlacementConfig.skippedPages)
        ? signaturePlacementConfig.skippedPages.filter((pageIndex) => typeof pageIndex === 'number')
        : [],
    } as SignaturePlacementConfig;
  }, [pageCount, signaturePlacementConfig]);

  const getPlacementForPage = useCallback((pageIndex: number): DraftSignaturePlacement | null => {
    if (!effectiveSignatureConfig) return null;
    if (effectiveSignatureConfig.markAllPages && effectiveSignatureConfig.skippedPages?.includes(pageIndex)) return null;
    if (!effectiveSignatureConfig.markAllPages) {
      return effectiveSignatureConfig.base.pageIndex === pageIndex ? effectiveSignatureConfig.base : null;
    }
    const override = effectiveSignatureConfig.overrides?.[String(pageIndex)];
    return override ? { ...sanitizeDraftSignaturePlacement(override, DEFAULT_SIGNATURE_PLACEMENT), pageIndex } : { ...sanitizeDraftSignaturePlacement(effectiveSignatureConfig.base, DEFAULT_SIGNATURE_PLACEMENT), pageIndex };
  }, [effectiveSignatureConfig]);

  useEffect(() => {
    if (!dragState || !enableDraftSignatureDrag || !onDraftSignaturePlacementChange) return;

    const onMove = (event: MouseEvent) => {
      const page = document.querySelector<HTMLElement>(`[data-preview-page-index="${dragState.pageIndex}"]`);
      if (!page) return;
      const rect = page.getBoundingClientRect();
      const activePlacement = getPlacementForPage(dragState.pageIndex) || DEFAULT_SIGNATURE_PLACEMENT;
      const renderedWidth = (activePlacement.width / LETTER_PAGE_WIDTH_PX) * rect.width;
      const renderedHeight = (activePlacement.height / LETTER_PAGE_HEIGHT_PX) * rect.height;
      const x = (Math.max(0, Math.min(rect.width - renderedWidth, event.clientX - rect.left - dragState.dx)) / Math.max(rect.width, 1)) * LETTER_PAGE_WIDTH_PX;
      const y = (Math.max(0, Math.min(rect.height - renderedHeight, event.clientY - rect.top - dragState.dy)) / Math.max(rect.height, 1)) * LETTER_PAGE_HEIGHT_PX;
      onDraftSignaturePlacementChange({
        pageIndex: dragState.pageIndex,
        x,
        y,
        width: activePlacement.width,
        height: activePlacement.height,
      });
    };

    const onUp = () => setDragState(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragState, enableDraftSignatureDrag, getPlacementForPage, onDraftSignaturePlacementChange]);

  const downloadPdfHandler = useCallback((mode: 'download' | 'blob' = 'download', options: PdfDownloadOptions = {}): Promise<void | Blob> => {
    if (!editor) return Promise.resolve();
    const opts = {
      title: title || 'Untitled Document',
      referenceNo,
      displayDate,
      pageWmm: dims.w,
      pageHmm: dims.h,
      pageCount,
      letterheadSafePx,
      footerSafePx,
      qrCodeData,
      approvedSignatureHtml: normalizedApprovedSignatureHtml,
      signaturePlacementConfig: effectiveSignatureConfig,
      pageGapPx: PAGE_GAP_PX,
      printSafeMarginMm: options.printSafeMarginMm,
    };
    // 'blob' resolves with the generated PDF bytes (read-only viewer building a
    // File); 'download' (default) saves to disk like the preview PDF button.
    if (mode === 'blob') {
      return exportPdfBlob(editor, opts);
    }
    downloadPdf(editor, opts);
    return Promise.resolve();
  }, [editor, title, referenceNo, displayDate, dims.w, dims.h, pageCount, letterheadSafePx, footerSafePx, qrCodeData, normalizedApprovedSignatureHtml, effectiveSignatureConfig]);

  // Expose the editor-accurate PDF export to parent components (e.g. the
  // CreateLetterPage header "Download PDF" button) so they produce the exact
  // same output as the preview pane's PDF button instead of a divergent render.
  const latestDownloadRef = useRef(downloadPdfHandler);
  useEffect(() => {
    latestDownloadRef.current = downloadPdfHandler;
  }, [downloadPdfHandler]);
  useEffect(() => {
    if (!registerPdfDownloader) return;
    const wrapper: PdfDownloaderFn = (mode, options) => latestDownloadRef.current?.(mode, options) ?? Promise.resolve();
    registerPdfDownloader(wrapper);
    return () => registerPdfDownloader(null);
  }, [registerPdfDownloader]);

  const renderSignatureOverlay = (pageIndex: number) => {
    const placement = getPlacementForPage(pageIndex);
    if (!placement) return null;
    const style = {
      left: `${(placement.x / LETTER_PAGE_WIDTH_PX) * 100}%`,
      top: `${(placement.y / LETTER_PAGE_HEIGHT_PX) * 100}%`,
      width: `${(placement.width / LETTER_PAGE_WIDTH_PX) * 100}%`,
      height: `${(placement.height / LETTER_PAGE_HEIGHT_PX) * 100}%`,
    };

    if (normalizedApprovedSignatureHtml) {
      // Scale the signature to FILL the placement box (anchored top-left) so the
      // signed signature fills the box the user drew — matching LetterDocument/QR
      // and the io.ts PDF — instead of sitting natural-size at the top.
      const fitScale = computeApprovedSignatureFitScale(normalizedApprovedSignatureHtml, placement);
      const approvedStyle = {
        left: style.left,
        top: style.top,
        transform: `scale(${fitScale})`,
        transformOrigin: 'top left' as const,
      };
      return (
        <div className="preview-signature-approved" style={approvedStyle} dangerouslySetInnerHTML={{ __html: normalizedApprovedSignatureHtml }} />
      );
    }

    return (
      <div
        className={`preview-signature-box${enableDraftSignatureDrag ? ' is-draggable' : ''}`}
        style={style}
        onMouseDown={(event) => {
          if (!enableDraftSignatureDrag || !onDraftSignaturePlacementChange) return;
          const rect = event.currentTarget.getBoundingClientRect();
          setDragState({
            pageIndex,
            dx: event.clientX - rect.left,
            dy: event.clientY - rect.top,
          });
        }}
      >
        Signature Box
        {enableDraftSignatureDrag && effectiveSignatureConfig?.markAllPages && onRemoveSignaturePage ? (
          <button
            type="button"
            title="Remove signature from this page"
            className="preview-signature-remove"
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              onRemoveSignaturePage(pageIndex);
            }}
          >×</button>
        ) : null}
      </div>
    );
  };

  return (
    <div className="preview-pane" aria-label="Preview">
      <div className="preview-header">
        <span>Preview</span>
        <div className="preview-actions">
          <button
            type="button"
            className="rb-btn sm preview-pdf-btn"
            onClick={() => { void downloadPdfHandler(); }}
            title="Download as PDF"
            onMouseDown={(e) => e.preventDefault()}
          >
            <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" style={{ verticalAlign: '-2px', marginRight: 4 }}>
              <path d="M8 1.5 V10.5 M5 7.5 L8 10.5 L11 7.5" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2.5 13.5 H13.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
            PDF
          </button>
        </div>
        <div className="preview-zoom">
          <button
            type="button"
            className="rb-btn sm"
            onClick={previewZoomOut}
            title="Zoom out"
            onMouseDown={(e) => e.preventDefault()}
          >−</button>
          <button
            type="button"
            className="zoom-readout"
            onClick={previewZoomReset}
            title="Reset zoom"
            onMouseDown={(e) => e.preventDefault()}
          >{Math.round(previewZoom * 100)}%</button>
          <button
            type="button"
            className="rb-btn sm"
            onClick={previewZoomIn}
            title="Zoom in"
            onMouseDown={(e) => e.preventDefault()}
          >+</button>
        </div>
      </div>
      <div className="preview-scroll">
        <div
          className="paper-stack preview-stack"
          data-pages={pageCount}
          style={{
            width: `${dims.w}mm`,
            transform: `scale(${previewZoom})`,
            transformOrigin: 'top left',
            ['--letterhead-safe-px' as string]: `${letterheadSafePx}px`,
            ['--footer-safe-px' as string]: `${footerSafePx}px`
          }}
        >
          {paginated &&
            Array.from({ length: pageCount }, (_, i) => (
              <div
                key={i}
                className="page-bg preview-page-bg"
                data-preview-page-index={i}
                style={{
                  height: `${dims.h}mm`,
                  marginBottom: i === pageCount - 1 ? 0 : `${PAGE_GAP_PX}px`
                }}
                aria-hidden="true"
              >
                <img
                  className="page-letterhead"
                  src={i === 0 ? '/letterhead.png' : '/letterhead2.png'}
                  alt=""
                  onLoad={(e) => handleLetterheadLoad(e.currentTarget)}
                />
                {i === 0 && (
                  <div className="page-letterhead-meta" aria-hidden="true">
                    <span>SEPL reference number: {referenceNo}</span>
                    <span>Date: {displayDate}</span>
                  </div>
                )}
                <img
                  className="page-footer"
                  src="/footer.png"
                  alt=""
                  onLoad={(e) => handleFooterLoad(e.currentTarget)}
                />
                {qrCodeData ? (
                  <div className="page-footer-qr" aria-label="QR Code">
                    <img src={qrCodeData} alt="QR Code" />
                  </div>
                ) : null}
                {renderSignatureOverlay(i)}
                <span className={`page-number${qrCodeData ? ' with-footer-qr' : ''}`}>{i + 1}</span>
              </div>
            ))}

          <div
            className="paper-content-wrap preview-content-wrap"
            style={
              paginated
                ? {
                    position: 'absolute',
                    inset: 0,
                    paddingTop: `${topReservePx}px`,
                    paddingRight: `${CONTENT_RIGHT_MM}mm`,
                    paddingBottom: `${bottomReservePx}px`,
                    paddingLeft: `${CONTENT_LEFT_MM}mm`
                  }
                : undefined
            }
          >
            <div
              className="paper preview-paper"
              data-size={pageSize}
              data-orient={orientation}
              style={
                paginated
                  ? undefined
                  : {
                      width: `${dims.w}mm`,
                      minHeight: `${dims.h}mm`,
                      paddingTop: `calc(${letterheadSafePx}px + ${CONTENT_AFTER_META_MM}mm)`,
                      paddingRight: `${CONTENT_RIGHT_MM}mm`,
                      paddingBottom: `${footerSafePx}px`,
                      paddingLeft: `${CONTENT_LEFT_MM}mm`,
                      position: 'relative'
                    }
              }
            >
              {!paginated && (
                <>
                  <img className="page-letterhead" src="/letterhead.png" alt="" onLoad={(e) => handleLetterheadLoad(e.currentTarget)} />
                  <div className="page-letterhead-meta" aria-hidden="true">
                    <span>SEPL reference number: {referenceNo}</span>
                    <span>Date: {displayDate}</span>
                  </div>
                  <img className="page-footer" src="/footer.png" alt="" onLoad={(e) => handleFooterLoad(e.currentTarget)} />
                  {qrCodeData ? (
                    <div className="page-footer-qr" aria-label="QR Code">
                      <img src={qrCodeData} alt="QR Code" />
                    </div>
                  ) : null}
                  {renderSignatureOverlay(0)}
                </>
              )}
              <div
                ref={contentRef}
                className="paper-content ProseMirror preview-content"
                dangerouslySetInnerHTML={{ __html: html || '<p></p>' }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
